import clsx from "clsx";
import { useEffect, useRef, type PointerEvent, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /** override the spotlight color. Pass an "R, G, B" string. */
  rgb?: string;
};

/**
 * Section-wide circular cursor spotlight in the style of inkwell.tech.
 *
 * The wrapper updates --ink-x / --ink-y CSS variables on pointer move.
 * Every descendant `.ink-card` reads those same variables and projects
 * its own radial highlight from the SECTION's coordinates — so the
 * spotlight feels continuous as the cursor crosses card boundaries.
 *
 * The CSS that does the heavy lifting lives in `index.css`.
 */
export function InkwellGroup({ children, className, rgb }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Cards inside this section need to know their offset from the group origin
    // so the radial centre lands at the cursor regardless of where the card sits.
    const cards = el.querySelectorAll<HTMLElement>(".ink-card");
    const calc = () => {
      const groupRect = el.getBoundingClientRect();
      cards.forEach((c) => {
        const r = c.getBoundingClientRect();
        c.style.setProperty("--ink-cardx", `${r.left - groupRect.left}px`);
        c.style.setProperty("--ink-cardy", `${r.top - groupRect.top}px`);
      });
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    window.addEventListener("scroll", calc, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", calc);
    };
  }, []);

  function onMove(e: PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--ink-x", `${e.clientX - r.left}px`);
    el.style.setProperty("--ink-y", `${e.clientY - r.top}px`);
  }

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      className={clsx("ink-group", className)}
      style={rgb ? ({ ["--ink-color" as string]: rgb } as React.CSSProperties) : undefined}
    >
      {children}
    </div>
  );
}
